import { type ChangeEvent, useMemo, useState } from "react";
import { FileEditAltIcon, notification, TextInput, ToggleInput } from "@datagame/ui";
import { string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { AttributesSection } from "../AttributesSection";
import {
  type Game,
  type LeaderboardConfigurationUpdateInput,
  type WordopolisConfigurationUpdateInput,
  WordopolisDifficulty,
} from "../../../../../../../gql/graphql";
import { isWordopolisGame } from "../../../../../../../graphql/helpers/isWordopolisGame";
import { Section } from "../../Common";
import { SelectInput } from "../../../../../../shared/ui/SelectInput";
import type { SelectOption } from "../../../../../../shared/ui/SelectInput/SelectInput";
import { SliderInput } from "../../../../../../shared/ui/SliderInput";
import {
  LEADERBOARD_CONFIGURATION_UPDATE,
  WORDOPOLIS_CONFIGURATION_UPDATE,
  WORDOPOLIS_CONFIGURATION_UPDATE_WITH_BOARD,
} from "../../../../../../../graphql/mutations/GameMutation";
import { isLeaderboardGame } from "../../../../../../../graphql/helpers/LeaderboardGame";
import { Accordion } from "../../../../../../shared/ui/Accordion";
import { LeaderboardConfigSection } from "../LeaderboardConfigSection";
import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";

import styles from "./WordopolisSettingsForm.module.scss";

export interface WordopolisSettingsFormProps {
  game: Game;
}
export const WordopolisSettingsForm = ({ game }: WordopolisSettingsFormProps) => {
  const { t } = useTranslation();
  const { project } = useProject();

  const [updateConfigMutation] = useMutation(WORDOPOLIS_CONFIGURATION_UPDATE);
  const [updateConfigWithBoardMutation] = useMutation(WORDOPOLIS_CONFIGURATION_UPDATE_WITH_BOARD);
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  const difficultyOptions = useMemo(() => {
    return [
      {
        value: WordopolisDifficulty.Easy,
        label: "Easy",
      },
      {
        value: WordopolisDifficulty.Medium,
        label: "Medium",
      },
      {
        value: WordopolisDifficulty.Hard,
        label: "Hard",
      },
    ];
  }, []);

  const [topic, setTopic] = useState<string>(isWordopolisGame(game) ? game.configuration.topic : "");
  const [difficulty, setDifficulty] = useState(
    isWordopolisGame(game)
      ? difficultyOptions.find((item) => item.value === game.configuration.difficulty)
      : difficultyOptions.find((item) => item.value === WordopolisDifficulty.Easy),
  );
  const [numberOfRows, setNumberOfRows] = useState<number>(
    isWordopolisGame(game) ? game.configuration.numberOfRows : 0,
  );
  const [numberOfColumns, setNumberOfColumns] = useState<number>(
    isWordopolisGame(game) ? game.configuration.numberOfColumns : 0,
  );
  const [startText, setStartText] = useState<string>(isWordopolisGame(game) ? game.configuration.startText : "");
  const [showWords, setShowWords] = useState<boolean>(isWordopolisGame(game) ? game.configuration.showWords : false);
  const [isTimeLimited, setIsTimeLimited] = useState<boolean>(
    isWordopolisGame(game) ? game.configuration.isTimeLimited : false,
  );
  const [timeLimit, setTimeLimit] = useState<number>(isWordopolisGame(game) ? game.configuration.limitedTime : 0);

  const [showLeaderboard, setShowLeaderboard] = useState(
    isLeaderboardGame(game) ? game.configuration.showLeaderboard : false,
  );

  const handleShowLeaderboardChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowLeaderboard(event.target.checked);

    updateConfig({
      showLeaderboard: event.target.checked,
    });
  };

  const handleLeaderboardConfigurationChange = (data: LeaderboardConfigurationUpdateInput) => {
    if (isLeaderboardGame(game) && project) {
      updateLeaderboardConfig({
        variables: {
          leaderboardConfigurationId: game?.configuration.leaderboardConfiguration?.id,
          data,
        },
        refetchQueries: [{ query: GAME_ALL, variables: { projectId: project.id } }],
      });
    }
  };

  const handleDifficultyChange = (item: SelectOption) => {
    const newValue = difficultyOptions.find((option) => {
      return option.value === item.value;
    });
    setDifficulty(newValue);

    updateConfig({ difficulty: newValue?.value });
  };

  const updateConfig = (
    data: WordopolisConfigurationUpdateInput,
    mutation: typeof updateConfigMutation | typeof updateConfigWithBoardMutation = updateConfigMutation,
  ) => {
    mutation({
      variables: {
        wordopolisConfigurationId: game.configuration.id,
        data,
      },
      onError: (error) => {
        notification.danger({
          title: t("common:error"),
          description: error.message,
        });
      },
    });
  };

  const handleSave = () => {
    updateConfig({ topic, startText, limitedTime: timeLimit });
  };

  const handleSaveWithBoard = () => {
    updateConfig(
      {
        numberOfRows,
        numberOfColumns,
      },
      updateConfigWithBoardMutation,
    );
  };

  const handleChangeShowWords = (event: ChangeEvent<HTMLInputElement>) => {
    setShowWords(event.target.checked);
    updateConfig({ showWords: event.target.checked });
  };

  const handleChangeIsTimeLimited = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTimeLimited(event.target.checked);
    updateConfig({ isTimeLimited: event.target.checked });
  };

  const attributeValidation = string()
    .trim()
    .matches(/^[A-Za-z]*$/, "Only letters are allowed")
    .min(2, "This attribute length is too short")
    .max(Math.max(numberOfColumns, numberOfRows), "This attribute length would not fit in the current board size")
    .required("This attribute is required");

  return (
    <div>
      <Section title={t("wordopolis:topic")} tooltip={<TooltipWithIcon content={t("wordopolis:topicTooltip")} />}>
        <TextInput
          icon={<FileEditAltIcon />}
          placeholder={t("wordopolis:topicPlaceholder") ?? ""}
          value={topic}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTopic(e.target.value)}
          onBlur={handleSave}
          size="small"
        />
      </Section>
      <Section
        title={t("wordopolis:gameDifficulty")}
        tooltip={<TooltipWithIcon content={t("wordopolis:gameDifficultyTooltip")} />}
      >
        <SelectInput options={difficultyOptions} onChange={handleDifficultyChange} selected={difficulty} />
      </Section>

      <AttributesSection game={game} validation={attributeValidation} isWordopolis />

      <div className={styles.accordionSection}>
        <Accordion title={t("wordopolis:gameplayRules")}>
          <div className={styles.sliders}>
            <Section
              title={t("wordopolis:numberOfRows")}
              className={styles.slider}
              headerClassName={styles.sliderHeader}
              tooltip={<TooltipWithIcon content={t("wordopolis:numberOfRowsTooltip")} />}
            >
              <SliderInput
                className={styles.sliderInput}
                value={numberOfRows}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNumberOfRows(parseInt(e.target.value))}
                onBlur={handleSaveWithBoard}
                onTouchEnd={handleSaveWithBoard}
                min={5}
                max={20}
              />
            </Section>
            <Section
              title={t("wordopolis:numberOfColumns")}
              className={styles.slider}
              headerClassName={styles.sliderHeader}
              tooltip={<TooltipWithIcon content={t("wordopolis:numberOfColumnsTooltip")} />}
            >
              <SliderInput
                className={styles.sliderInput}
                value={numberOfColumns}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNumberOfColumns(parseInt(e.target.value))}
                onBlur={handleSaveWithBoard}
                onTouchEnd={handleSaveWithBoard}
                min={5}
                max={20}
              />
            </Section>
          </div>

          <Section
            title={t("wordopolis:startText")}
            tooltip={<TooltipWithIcon content={t("wordopolis:startTextTooltip")} />}
          >
            <TextInput
              icon={<FileEditAltIcon />}
              size="small"
              value={startText}
              onBlur={handleSave}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setStartText(e.target.value)}
            />
          </Section>
          <Section title="">
            <ToggleInput
              name="show-words"
              value={showWords}
              onChange={handleChangeShowWords}
              label={t("wordopolis:showWords") ?? ""}
              tooltip={<TooltipWithIcon content={t("wordopolis:showWordsTooltip")} />}
            />
          </Section>

          <Section title="">
            <ToggleInput
              name="is-time-limited"
              value={isTimeLimited}
              onChange={handleChangeIsTimeLimited}
              label={t("wordopolis:isTimeLimited") ?? ""}
              tooltip={<TooltipWithIcon content={t("wordopolis:isTimeLimitedTooltip")} />}
            />
          </Section>
          {isTimeLimited && (
            <Section
              title={t("wordopolis:timeLimit")}
              tooltip={<TooltipWithIcon content={t("wordopolis:timeLimitTooltip")} />}
            >
              <TextInput
                icon={<FileEditAltIcon />}
                size="small"
                type="number"
                value={timeLimit}
                onBlur={handleSave}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setTimeLimit(parseInt(e.target.value))}
              />
            </Section>
          )}
        </Accordion>
      </div>

      <Section title={t("project:gameSettings.form.preferences")}>
        <ToggleInput
          className={styles.toggleWrapper}
          value={showLeaderboard}
          name="show-leaderboard"
          label="Show leaderboard"
          onChange={handleShowLeaderboardChange}
          tooltip={<TooltipWithIcon content={t("project:gameSettings.form.showLeaderboardTooltip")} />}
        />

        {showLeaderboard && (
          <div className={styles.leaderboardSection}>
            <Accordion title={t("project:gameSettings.form.leaderboardConfig")}>
              {isLeaderboardGame(game) && (
                <LeaderboardConfigSection
                  leaderboardConfiguration={game.configuration.leaderboardConfiguration}
                  onUpdate={handleLeaderboardConfigurationChange}
                />
              )}
            </Accordion>
          </div>
        )}
      </Section>
    </div>
  );
};
